// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// "AI answer" in Search everything: the question goes to Alpha's chat API (Flameco AI)
// with the same token, app and page context the page's Flameco AI widget uses (so
// Alpha's own access check applies), and the answer streams in at the top of
// the results. Approvals and longer conversations continue in the Flameco AI chat.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dougConnection, openDougThread } from './Doug';
import { IconSparkles } from './icons';

/** What the person sees while Alpha works, in plain words (same as the widget). */
const DOING: Record<string, string> = {
  operations: 'Checking NetSuite…',
  engineering: 'Looking at engineering data…',
  documents: 'Reading documents…',
  customer: 'Looking up the customer…',
  microsoft365: 'Checking Microsoft 365…',
  manufacturing: 'Checking shop-floor records…',
  analytics: 'Crunching the numbers…',
  app_guide: 'Checking how this app works…',
};

type Status = 'idle' | 'thinking' | 'streaming' | 'done' | 'error' | 'denied' | 'stopped';

export interface DougTurn {
  question: string;
  text: string;
  doing: string;
  status: Status;
  /** A page of this app Flameco AI wants to open. */
  navTo: string | null;
  /** Flameco AI wants to change something: that needs the chat, where the OK button lives. */
  needsApproval: boolean;
  seconds: number | null;
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/**
 * Links in answers: only this app's paths and Flameco sites become clickable,
 * so an answer can never send someone to an arbitrary site.
 */
function safeHref(raw: string): string | null {
  const href = raw.replace(/&amp;/g, '&');
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  try {
    const u = new URL(href);
    const flameco = u.protocol === 'https:' && (u.hostname === 'flameco.com' || u.hostname.endsWith('.flameco.com'));
    const dev = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
    return flameco || dev ? u.href : null;
  } catch {
    return null;
  }
}

/** Flameco AI's markdown (bold, code, links, lists, tables, paragraphs) as safe HTML. */
export function dougMarkdown(src: string): string {
  const inline = (s: string) =>
    s
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\[([^\]]+)\]\(((?:https?:\/\/|\/)[^\s)]*)\)/g, (_m, label: string, href: string) => {
        const ok = safeHref(href);
        return ok ? `<a href="${esc(ok)}" data-doug-link>${label}</a>` : label;
      });
  let html = '';
  let list = false;
  let table: string[][] | null = null;
  const flush = () => {
    if (!table) return;
    const [head, ...body] = table.filter((r) => !r.every((c) => /^:?-{2,}:?$/.test(c)));
    html +=
      `<div class="fl-dtable"><table><thead><tr>${(head ?? []).map((c) => `<th>${c}</th>`).join('')}</tr></thead><tbody>` +
      body.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('') +
      '</tbody></table></div>';
    table = null;
  };
  for (const raw of esc(src).split('\n')) {
    const line = inline(raw);
    if (/^\s*\|.*\|\s*$/.test(line)) {
      if (list) {
        html += '</ul>';
        list = false;
      }
      (table ??= []).push(line.trim().slice(1, -1).split('|').map((c) => c.trim()));
      continue;
    }
    flush();
    const m = line.match(/^\s*[-*•]\s+(.*)$/) || line.match(/^\s*\d+\.\s+(.*)$/);
    if (m) {
      if (!list) {
        html += '<ul>';
        list = true;
      }
      html += `<li>${m[1]}</li>`;
      continue;
    }
    if (list) {
      html += '</ul>';
      list = false;
    }
    if (line.trim()) html += `<p>${line.replace(/^#{1,4}\s+/, '')}</p>`;
  }
  flush();
  return html + (list ? '</ul>' : '');
}

const EMPTY: DougTurn = { question: '', text: '', doing: '', status: 'idle', navTo: null, needsApproval: false, seconds: null };

/** One conversation with Flameco AI inside the search box; follow-ups keep the thread. */
export function useDougAnswer() {
  const [turn, setTurn] = useState<DougTurn>(EMPTY);
  const threadRef = useRef<string | null>(null);
  /** Earlier finished turns in this conversation, for the hand-over to the chat. */
  const historyRef = useRef<{ question: string; text: string }[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => abortRef.current?.abort(), []);
  const reset = useCallback(() => {
    abortRef.current?.abort();
    threadRef.current = null;
    historyRef.current = [];
    setTurn(EMPTY);
  }, []);
  useEffect(() => () => abortRef.current?.abort(), []);

  const ask = useCallback(async (question: string) => {
    const conn = dougConnection();
    const q = question.trim();
    if (!conn || !q) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const t0 = performance.now();
    threadRef.current ||= `s_${Array.from(crypto.getRandomValues(new Uint32Array(2))).join('').slice(0, 16)}`;
    setTurn((prev) => {
      if (prev.question && prev.text && prev.status !== 'denied' && prev.status !== 'error') historyRef.current.push({ question: prev.question, text: prev.text });
      return { ...EMPTY, question: q, status: 'thinking', doing: 'Thinking…' };
    });
    let text = '';
    try {
      const res = await fetch(`${conn.api.replace(/\/+$/, '')}/api/v1/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${conn.getToken() ?? ''}`, 'X-Alpha-App': conn.app, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, thread_id: threadRef.current, context: conn.context }),
        signal: ctrl.signal,
      });
      if (res.status === 401 || res.status === 403) {
        let detail = '';
        try {
          detail = (await res.json()).detail || '';
        } catch {
          /* not JSON */
        }
        setTurn((t) => ({ ...t, status: 'denied', text: detail || "You don't have access to Flameco AI here." }));
        return;
      }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      threadRef.current = res.headers.get('X-Alpha-Thread-Id') || threadRef.current;
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          // Alpha streams "code:json" lines: 0 = text, 2 = events, 3 = error.
          const i = line.indexOf(':');
          if (i < 0) continue;
          const code = line.slice(0, i);
          let v: any;
          try {
            v = JSON.parse(line.slice(i + 1));
          } catch {
            continue;
          }
          if (code === '0') {
            text += v;
            setTurn((t) => ({ ...t, text, status: 'streaming', doing: '' }));
          } else if (code === '2' && Array.isArray(v)) {
            for (const e of v) {
              if (e.type === 'agent_start' && !text) setTurn((t) => ({ ...t, doing: DOING[e.agent] || 'Working on it…' }));
              else if (e.type === 'answer_reset') {
                text = '';
                setTurn((t) => ({ ...t, text: '' }));
              } else if (e.type === 'approval_required') setTurn((t) => ({ ...t, needsApproval: true }));
              else if (e.type === 'navigate' && typeof e.path === 'string' && e.path.startsWith('/')) setTurn((t) => ({ ...t, navTo: e.path }));
            }
          } else if (code === '3') {
            text += `\n\nSorry, something went wrong: ${v}`;
            setTurn((t) => ({ ...t, text }));
          }
        }
      }
      const seconds = (performance.now() - t0) / 1000;
      setTurn((t) => ({
        ...t,
        status: 'done',
        doing: '',
        seconds,
        text: t.text || (t.needsApproval ? '' : "I couldn't find an answer to that. Could you say it another way?"),
      }));
    } catch (e: any) {
      if (e?.name === 'AbortError') setTurn((t) => ({ ...t, status: 'stopped', doing: '' }));
      else setTurn((t) => ({ ...t, status: 'error', doing: '', text: "I can't reach Flameco's systems right now. Please try again in a moment." }));
    }
  }, []);

  /** 👍 / 👎 on the last answer (Alpha's feedback endpoint). */
  const rate = useCallback(async (rating: 1 | -1) => {
    const conn = dougConnection();
    if (!conn || !threadRef.current) return false;
    try {
      const res = await fetch(`${conn.api.replace(/\/+$/, '')}/api/v1/chat/feedback`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${conn.getToken() ?? ''}`, 'X-Alpha-App': conn.app, 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id: threadRef.current, rating }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  /** Carry on in the Flameco AI widget, in the same conversation. */
  const continueInChat = useCallback((t: DougTurn) => {
    const turns = [...historyRef.current, ...(t.text ? [{ question: t.question, text: t.text }] : [])];
    openDougThread(
      threadRef.current,
      turns.map((x) => ({ question: x.question, answerHtml: dougMarkdown(x.text) })),
    );
  }, []);

  return useMemo(() => ({ turn, ask, stop, reset, rate, continueInChat }), [turn, ask, stop, reset, rate, continueInChat]);
}

/**
 * "AI answer" at the top of the search results, like a search engine's AI
 * overview: a short answer for what was typed, clipped until "Show more",
 * with links kept to this app and Flameco sites. Before an answer is asked
 * for it is a single line that asks for one.
 */
export function AiAnswerCard({
  turn,
  query,
  onAsk,
  onStop,
  onRate,
  onContinue,
  onLink,
  askKeys,
}: {
  /** The answer so far; null until one is asked for. */
  turn: DougTurn | null;
  /** What is typed in the box (asked about when the line is clicked). */
  query: string;
  onAsk: () => void;
  onStop: () => void;
  onRate: (rating: 1 | -1) => Promise<boolean>;
  onContinue: (t: DougTurn) => void;
  /** A link in the answer was clicked (this app's path or a Flameco address). */
  onLink: (href: string) => void;
  /** Modifier key shown on the ask line, e.g. "Ctrl". */
  askKeys?: string;
}) {
  const [rated, setRated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const question = turn?.question ?? '';
  useEffect(() => {
    setRated(null);
    setCopied(false);
    setOpen(false);
  }, [question]);
  // Long answers stay short until "Show more".
  useEffect(() => {
    const el = bodyRef.current;
    setClipped(!!el && el.scrollHeight > el.clientHeight + 4);
  }, [turn?.text, turn?.status, open]);

  if (!turn || turn.status === 'idle') {
    return (
      <button type="button" className="fl-ai fl-ai--ask" onClick={onAsk}>
        <span className="fl-ai__spark" aria-hidden="true">
          <IconSparkles className="fl-icon" />
        </span>
        <span className="fl-ai__asktext">
          <span className="fl-ai__label">Flameco AI</span>
          <span className="fl-ai__askq">
            Ask Flameco AI: <q>{query}</q>
          </span>
        </span>
        {askKeys && (
          <span className="fl-ai__keys" aria-hidden="true">
            <kbd className="fl-key">{askKeys}</kbd>
            <kbd className="fl-key">Enter</kbd>
          </span>
        )}
      </button>
    );
  }

  const busy = turn.status === 'thinking' || turn.status === 'streaming';
  const muted = turn.status === 'denied' || turn.status === 'error';
  return (
    <section className="fl-ai" aria-live="polite" aria-label="Flameco AI answer">
      <div className="fl-ai__head">
        <span className="fl-ai__spark" aria-hidden="true">
          <IconSparkles className="fl-icon" />
        </span>
        <span className="fl-ai__label">Flameco AI</span>
        {question !== query.trim() && <span className="fl-ai__about">about “{question}”</span>}
        {busy && (
          <button type="button" className="fl-sact fl-ai__stop" onClick={onStop}>
            Stop
          </button>
        )}
      </div>
      {!turn.text && busy ? (
        <div className="fl-ai__skel" role="status">
          <i />
          <i />
          <i />
          <span className="fl-ai__doing">{turn.doing || 'Thinking…'}</span>
        </div>
      ) : turn.status === 'stopped' && !turn.text ? (
        <p className="fl-ai__muted">Stopped.</p>
      ) : (
        <div
          ref={bodyRef}
          className={`fl-ai__body${open ? ' is-open' : ''}${clipped && !open ? ' is-clipped' : ''}${muted ? ' fl-ai__body--muted' : ''}`}
          onClick={(e) => {
            const a = (e.target as HTMLElement).closest('a[data-doug-link]') as HTMLAnchorElement | null;
            if (!a) return;
            e.preventDefault();
            onLink(a.getAttribute('href') || '');
          }}
        >
          <div className="fl-doug__md" dangerouslySetInnerHTML={{ __html: dougMarkdown(turn.text) }} />
          {turn.text && turn.doing && <div className="fl-ai__doing">{turn.doing}</div>}
        </div>
      )}
      {(clipped || open) && (
        <button type="button" className="fl-ai__more" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? 'Show less' : 'Show more'}
          <span className={`fl-ai__chev${open ? ' is-up' : ''}`} aria-hidden="true">
            ⌄
          </span>
        </button>
      )}
      {turn.needsApproval && (
        <div className="fl-doug__approve">
          This needs a change in NetSuite, which needs your OK in the chat.
          <button type="button" className="fl-sact fl-sact--primary" onClick={() => onContinue(turn)}>
            Review in chat
          </button>
        </div>
      )}
      {!busy && (
        <div className="fl-ai__foot">
          {turn.navTo && (
            <button type="button" className="fl-sact fl-sact--primary" onClick={() => onLink(turn.navTo!)}>
              Open that page
            </button>
          )}
          {turn.status === 'done' && turn.text && (
            <>
              <button type="button" className="fl-sact" onClick={() => onContinue(turn)}>
                Dive deeper in chat
              </button>
              <button
                type="button"
                className="fl-sact"
                onClick={() => {
                  void navigator.clipboard?.writeText(turn.text).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              {rated ? (
                <span className="fl-ai__muted">{rated}</span>
              ) : (
                <span className="fl-doug__rate">
                  <button type="button" className="fl-sact fl-sact--quiet-on" aria-label="Helpful" title="Helpful" onClick={async () => setRated((await onRate(1)) ? 'Thanks!' : "Couldn't save that")}>
                    👍
                  </button>
                  <button type="button" className="fl-sact fl-sact--quiet-on" aria-label="Not right" title="Not right" onClick={async () => setRated((await onRate(-1)) ? 'Thanks, noted.' : "Couldn't save that")}>
                    👎
                  </button>
                </span>
              )}
            </>
          )}
          <span className="fl-ai__note">
            AI can make mistakes. Check important numbers.
            {turn.seconds != null && turn.status === 'done' ? ` · ${turn.seconds.toFixed(1)} s` : ''}
          </span>
        </div>
      )}
    </section>
  );
}
